<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractGeneralInfo',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_6379803495938895_to = computed(() => ({
    name: 'app.company',
    params: {
      id: props.contract.company.id,
    },
  }));
  const b_8906220998092919_to = computed(() => ({
    name: 'app.company',
    params: {
      id: props.contract.counterparty.id,
    },
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_6379803495938895_to: b_6379803495938895_to.value,
    b_8906220998092919_to: b_8906220998092919_to.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_6379803495938895_to', nodeId: '6379803495938895' },
      { name: 'b_8906220998092919_to', nodeId: '8906220998092919' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_6379803495938895_to, b_8906220998092919_to },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_emit_40i3msi8w_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_mkylx4bgq_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_pudv87rfx_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'general',
        contract: _.cloneDeep(props.contract),
      },
    };
  };
  const act_7349226261686150_emit_85oj2a0va_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5937240531526321_emit_e97v9l9tl_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_5937240531526321_close_6f7nzhejd_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_5937240531526321_showModal_6j4ahfmbl_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'company',
        contract: _.cloneDeep(props.contract),
      },
    };
  };
  const act_7527485200259320_emit_dyvtwwmz1_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_7527485200259320_close_d6kaqmrgi_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_7527485200259320_showModal_317m4ivcy_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'counterparty',
        contract: _.cloneDeep(props.contract),
      },
    };
  };
  const act_7127218473441393_emit_h3u7uz5y3_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_7127218473441393_close_t6res1dvr_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_7127218473441393_showModal_x8wgxir7r_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'rental',
        contract: props.contract.rental
          ? _.cloneDeep(props.contract)
          : _.extend(_.cloneDeep(props.contract), {
              rental: {
                id: null,
                name: null,
                number: null,
                description: null,
                house_number: null,
                inactive: false,
                location_code: null,
                lot_number: null,
              },
            }),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_469hjtmf2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8uo6tvk9h =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_469hjtmf2,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8uo6tvk9h],
    event: 'reload',
  });
  const act_2095089031469157_emit_40i3msi8w = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_40i3msi8w_getActionArgs,
  });
  const inst_evh_2095089031469157_close_mkylx4bgq =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_40i3msi8w,
      event: 'close',
      when: inst_evh_2095089031469157_close_mkylx4bgq_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_pudv87rfx =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_pudv87rfx_getActionArgs,
      events: [inst_evh_2095089031469157_close_mkylx4bgq],
    });
  const inst_evh_2095089031469157_clickPreventStop_w003grhx6 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_pudv87rfx,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_w003grhx6],
      event: 'click.prevent.stop',
    });
  const act_7349226261686150_emit_85oj2a0va = new actionTypes.EmitAction({
    actionArgs: act_7349226261686150_emit_85oj2a0va_getActionArgs,
  });
  const inst_evh_7349226261686150_reload_r53bjhf8h =
    new actionTypes.EventHandlerImpl({
      action: act_7349226261686150_emit_85oj2a0va,
      event: 'reload',
      displayName: 'emit',
    });
  const inst_evh_7349226261686150_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7349226261686150_reload_r53bjhf8h],
    event: 'reload',
  });
  const act_5937240531526321_emit_e97v9l9tl = new actionTypes.EmitAction({
    actionArgs: act_5937240531526321_emit_e97v9l9tl_getActionArgs,
  });
  const inst_evh_5937240531526321_close_6f7nzhejd =
    new actionTypes.EventHandlerImpl({
      action: act_5937240531526321_emit_e97v9l9tl,
      event: 'close',
      when: inst_evh_5937240531526321_close_6f7nzhejd_getWhen,
      displayName: 'emit',
    });
  const act_5937240531526321_showModal_6j4ahfmbl =
    new actionTypes.ShowModalAction({
      actionArgs: act_5937240531526321_showModal_6j4ahfmbl_getActionArgs,
      events: [inst_evh_5937240531526321_close_6f7nzhejd],
    });
  const inst_evh_5937240531526321_clickPreventStop_2bkkjjbje =
    new actionTypes.EventHandlerImpl({
      action: act_5937240531526321_showModal_6j4ahfmbl,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5937240531526321_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5937240531526321_clickPreventStop_2bkkjjbje],
      event: 'click.prevent.stop',
    });
  const act_7527485200259320_emit_dyvtwwmz1 = new actionTypes.EmitAction({
    actionArgs: act_7527485200259320_emit_dyvtwwmz1_getActionArgs,
  });
  const inst_evh_7527485200259320_close_d6kaqmrgi =
    new actionTypes.EventHandlerImpl({
      action: act_7527485200259320_emit_dyvtwwmz1,
      event: 'close',
      when: inst_evh_7527485200259320_close_d6kaqmrgi_getWhen,
      displayName: 'emit',
    });
  const act_7527485200259320_showModal_317m4ivcy =
    new actionTypes.ShowModalAction({
      actionArgs: act_7527485200259320_showModal_317m4ivcy_getActionArgs,
      events: [inst_evh_7527485200259320_close_d6kaqmrgi],
    });
  const inst_evh_7527485200259320_clickPreventStop_00z5hfopk =
    new actionTypes.EventHandlerImpl({
      action: act_7527485200259320_showModal_317m4ivcy,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_7527485200259320_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7527485200259320_clickPreventStop_00z5hfopk],
      event: 'click.prevent.stop',
    });
  const act_7127218473441393_emit_h3u7uz5y3 = new actionTypes.EmitAction({
    actionArgs: act_7127218473441393_emit_h3u7uz5y3_getActionArgs,
  });
  const inst_evh_7127218473441393_close_t6res1dvr =
    new actionTypes.EventHandlerImpl({
      action: act_7127218473441393_emit_h3u7uz5y3,
      event: 'close',
      when: inst_evh_7127218473441393_close_t6res1dvr_getWhen,
      displayName: 'emit',
    });
  const act_7127218473441393_showModal_x8wgxir7r =
    new actionTypes.ShowModalAction({
      actionArgs: act_7127218473441393_showModal_x8wgxir7r_getActionArgs,
      events: [inst_evh_7127218473441393_close_t6res1dvr],
    });
  const inst_evh_7127218473441393_clickPreventStop_dvx57xns0 =
    new actionTypes.EventHandlerImpl({
      action: act_7127218473441393_showModal_x8wgxir7r,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_7127218473441393_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7127218473441393_clickPreventStop_dvx57xns0],
      event: 'click.prevent.stop',
    });
  const act_4198689235226718_reloadComponentData_qyoyjy9hl =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_4198689235226718_reload_ku2kfdjee =
    new actionTypes.EventHandlerImpl({
      action: act_4198689235226718_reloadComponentData_qyoyjy9hl,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_4198689235226718_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4198689235226718_reload_ku2kfdjee],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_8uo6tvk9h,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_mkylx4bgq,
    inst_evh_2095089031469157_clickPreventStop_w003grhx6,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_7349226261686150_reload_r53bjhf8h,
    inst_evh_7349226261686150_reload,
    inst_evh_5937240531526321_close_6f7nzhejd,
    inst_evh_5937240531526321_clickPreventStop_2bkkjjbje,
    inst_evh_5937240531526321_clickPreventStop,
    inst_evh_7527485200259320_close_d6kaqmrgi,
    inst_evh_7527485200259320_clickPreventStop_00z5hfopk,
    inst_evh_7527485200259320_clickPreventStop,
    inst_evh_7127218473441393_close_t6res1dvr,
    inst_evh_7127218473441393_clickPreventStop_dvx57xns0,
    inst_evh_7127218473441393_clickPreventStop,
    inst_evh_4198689235226718_reload_ku2kfdjee,
    inst_evh_4198689235226718_reload,
  };

  const evh_7315092382398562_reload_8uo6tvk9h = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8uo6tvk9h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_mkylx4bgq = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_mkylx4bgq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_w003grhx6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_w003grhx6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7349226261686150_reload_r53bjhf8h = async (event, scope) => {
    return await actions.inst_evh_7349226261686150_reload_r53bjhf8h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7349226261686150_reload = async (event, scope) => {
    return await actions.inst_evh_7349226261686150_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5937240531526321_close_6f7nzhejd = async (event, scope) => {
    return await actions.inst_evh_5937240531526321_close_6f7nzhejd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5937240531526321_clickPreventStop_2bkkjjbje = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5937240531526321_clickPreventStop_2bkkjjbje.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5937240531526321_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5937240531526321_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7527485200259320_close_d6kaqmrgi = async (event, scope) => {
    return await actions.inst_evh_7527485200259320_close_d6kaqmrgi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7527485200259320_clickPreventStop_00z5hfopk = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7527485200259320_clickPreventStop_00z5hfopk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7527485200259320_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7527485200259320_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7127218473441393_close_t6res1dvr = async (event, scope) => {
    return await actions.inst_evh_7127218473441393_close_t6res1dvr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7127218473441393_clickPreventStop_dvx57xns0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7127218473441393_clickPreventStop_dvx57xns0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7127218473441393_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7127218473441393_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4198689235226718_reload_ku2kfdjee = async (event, scope) => {
    return await actions.inst_evh_4198689235226718_reload_ku2kfdjee.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4198689235226718_reload = async (event, scope) => {
    return await actions.inst_evh_4198689235226718_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Contract', 'General') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Contract number')"
          :content="contract.contract_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Contract', 'Contract type')">
          <UcContractTypeBadge :value="contract.contract_type">
          </UcContractTypeBadge>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Contract', 'Status')">
          <UcContractStatusCard
            :contract="contract"
            :reload="lastLoaded"
            @reload="evh_7349226261686150_reload($event, {})"
          >
          </UcContractStatusCard>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Description')"
          :content="contract.extras.description"
          v-if="contract.extras.description"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Folder number')"
          :content="contract.extras.folder_number"
          v-if="contract.extras.folder_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Reference number')"
          :content="contract.extras.reference_number"
          v-if="contract.extras.reference_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Lessor')"
          expandable
        >
          <ItpBox>
            <ItpLink :text="contract.company.name" :to="b_6379803495938895_to">
            </ItpLink>
            <ItpButton
              :text="gettext('Edit...')"
              variant="light"
              size="sm"
              class="ms-auto"
              v-if="!readonly"
              @click.prevent.stop="
                evh_5937240531526321_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </ItpBox>
          <template #expander>
            <UcContractCompanyCard :item="contract.company">
            </UcContractCompanyCard>
          </template>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Tenant')"
          expandable
        >
          <ItpBox>
            <ItpLink
              :text="contract.counterparty.name"
              :to="b_8906220998092919_to"
            >
            </ItpLink>
            <ItpButton
              :text="gettext('Edit...')"
              variant="light"
              size="sm"
              class="ms-auto"
              v-if="!readonly"
              @click.prevent.stop="
                evh_7527485200259320_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </ItpBox>
          <template #expander>
            <UcContractClientCard :item="contract.counterparty">
            </UcContractClientCard>
          </template>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Rental')"
          expandable
          :expander-cols="11"
        >
          <ItpBox>
            <ItpLink
              :text="contract.rental.name"
              :to="{
                name: 'app.rental',
                params: {
                  id: contract.rental.id,
                },
              }"
              v-if="contract.rental"
            >
            </ItpLink>
            <ItpButton
              :text="gettext('Edit...')"
              variant="light"
              size="sm"
              class="ms-auto"
              v-if="!readonly"
              @click.prevent.stop="
                evh_7127218473441393_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </ItpBox>
          <template #expander>
            <UcContractRentalCard
              :rental="contract.rental"
              v-if="contract.rental"
            >
            </UcContractRentalCard>
            <UcContractRentalServices
              :rental="contract.rental"
              :reload="lastLoaded"
              v-if="contract.rental"
              @reload="evh_4198689235226718_reload($event, {})"
            >
            </UcContractRentalServices>
          </template>
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
